import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

interface Screenshot {
  id: string;
  jobId: string;
  type: 'original' | 'processed';
  timestamp: number;
  filePath: string;
  createdAt: string;
}

interface ComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenshots: Screenshot[];
  selectedTimestamp?: number;
  jobId: string;
}

const ComparisonModal: React.FC<ComparisonModalProps> = ({
  isOpen,
  onClose,
  screenshots,
  selectedTimestamp,
  jobId
}) => {
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentTimestamp, setCurrentTimestamp] = useState(selectedTimestamp || 0);
  const containerRef = useRef<HTMLDivElement>(null);

  const originalScreenshot = screenshots.find(s => s.type === 'original' && s.timestamp === currentTimestamp);
  const processedScreenshot = screenshots.find(s => s.type === 'processed' && s.timestamp === currentTimestamp);

  const availableTimestamps = Array.from(new Set(screenshots.map(s => s.timestamp))).sort();

  useEffect(() => {
    if (selectedTimestamp !== undefined) {
      setCurrentTimestamp(selectedTimestamp);
    }
  }, [selectedTimestamp]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.5, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.5, 0.5));
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          handleReset();
          break;
        case 'ArrowLeft':
          const prevIndex = availableTimestamps.indexOf(currentTimestamp) - 1;
          if (prevIndex >= 0) {
            setCurrentTimestamp(availableTimestamps[prevIndex]);
          }
          break;
        case 'ArrowRight':
          const nextIndex = availableTimestamps.indexOf(currentTimestamp) + 1;
          if (nextIndex < availableTimestamps.length) {
            setCurrentTimestamp(availableTimestamps[nextIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose, handleZoomIn, handleZoomOut, handleReset, currentTimestamp, availableTimestamps]);

  if (!isOpen) return null;

  const formatTimestamp = (timestamp: number) => {
    const minutes = Math.floor(timestamp / 60);
    const seconds = Math.floor(timestamp % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-retro-surface border-b-2 border-retro-line p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-retro-pixel text-lg text-retro-primary">
              COMPARAÇÃO DE SCREENSHOTS
            </h2>

            {/* Timestamp selector */}
            <div className="flex items-center gap-2">
              <span className="text-retro-text text-sm">Posição:</span>
              <select
                value={currentTimestamp}
                onChange={(e) => setCurrentTimestamp(Number(e.target.value))}
                className="retro-input px-2 py-1 text-sm"
              >
                {availableTimestamps.map(timestamp => (
                  <option key={timestamp} value={timestamp}>
                    {formatTimestamp(timestamp)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="retro-button retro-button-secondary px-3 py-2"
              title="Diminuir zoom (-)"
            >
              <ZoomOut size={16} />
            </button>

            <span className="text-retro-text text-sm px-2">
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={handleZoomIn}
              className="retro-button retro-button-secondary px-3 py-2"
              title="Aumentar zoom (+)"
            >
              <ZoomIn size={16} />
            </button>

            <button
              onClick={handleReset}
              className="retro-button retro-button-secondary px-3 py-2"
              title="Resetar visualização (0)"
            >
              <RotateCcw size={16} />
            </button>

            <button
              onClick={onClose}
              className="retro-button retro-button-danger px-3 py-2"
              title="Fechar (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Comparison Container */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center mt-20 mb-8 px-4 overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        <div className="flex gap-4 max-w-full max-h-full">
          {/* Original */}
          <div className="flex-1 flex flex-col items-center">
            <div className="retro-card p-2 border-2 border-retro-primary mb-2">
              <span className="text-retro-pixel text-sm text-retro-primary">ORIGINAL</span>
            </div>

            {originalScreenshot ? (
              <div className="relative border-2 border-retro-line rounded-retro overflow-hidden bg-retro-surface">
                <img
                  src={`/api/screenshots/${originalScreenshot.filePath.split('/').pop()}`}
                  alt="Screenshot original"
                  className="max-w-none transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    maxHeight: 'calc(100vh - 200px)',
                    maxWidth: '45vw'
                  }}
                  draggable={false}
                />
              </div>
            ) : (
              <div className="retro-card p-8 text-center text-retro-muted border-2 border-retro-line-soft">
                <span className="text-retro-pixel">📷</span>
                <p className="mt-2">Screenshot original não disponível</p>
              </div>
            )}
          </div>

          {/* Processed */}
          <div className="flex-1 flex flex-col items-center">
            <div className="retro-card p-2 border-2 border-retro-accent mb-2">
              <span className="text-retro-pixel text-sm text-retro-accent">PROCESSADO</span>
            </div>

            {processedScreenshot ? (
              <div className="relative border-2 border-retro-line rounded-retro overflow-hidden bg-retro-surface">
                <img
                  src={`/api/screenshots/${processedScreenshot.filePath.split('/').pop()}`}
                  alt="Screenshot processado"
                  className="max-w-none transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    maxHeight: 'calc(100vh - 200px)',
                    maxWidth: '45vw'
                  }}
                  draggable={false}
                />
              </div>
            ) : (
              <div className="retro-card p-8 text-center text-retro-muted border-2 border-retro-line-soft">
                <span className="text-retro-pixel">📷</span>
                <p className="mt-2">Screenshot processado não disponível</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer with instructions */}
      <div className="absolute bottom-0 left-0 right-0 bg-retro-surface border-t-2 border-retro-line p-2">
        <div className="text-center text-retro-muted text-xs">
          <span className="text-retro-pixel">CONTROLES:</span>
          {' '}ESC: Fechar • +/-: Zoom • 0: Resetar • ←→: Navegar screenshots • Arrastar: Mover quando com zoom
        </div>
      </div>
    </div>
  );
};

export default ComparisonModal;